import {memo, ReactNode, useState} from "react";
import {Box, ButtonGroup, HStack, Icon, IconButton, Input, Table, Text, Textarea} from "@chakra-ui/react";
import {CheckIcon, PencilSquareIcon, PlusIcon, SparklesIcon, TrashIcon} from "@heroicons/react/16/solid";
import {Button} from "../../../../../components/ui/button";
import {Checkbox} from "../../../../../components/ui/checkbox";
import {NativeSelectField, NativeSelectRoot} from "../../../../../components/ui/native-select";
import {FieldType} from "../pages/populationsData";
import {FieldTypeBadge} from "./FieldTypeBadge";
import {LockHint} from "./LockHint";

const TYPE_OPTIONS: {value: FieldType; label: string}[] = [
  {value: "text", label: "Text"},
  {value: "date", label: "Date"},
  {value: "number", label: "Number"},
  {value: "currency", label: "Currency"},
  {value: "boolean", label: "Boolean"},
];

/** A single editable field row — read mode (static + Edit/Delete) and edit mode (inputs + Save/Cancel). */
export type EditableFieldRowData = {id: string; name: string; type: FieldType; hint?: string; isKey?: boolean};

const FieldRow = memo(({row, showKey, locked}: {row: EditableFieldRowData; showKey: boolean; locked: boolean}) => {
  const [editing, setEditing] = useState(false);

  // Locked fields never enter edit mode, so this branch is unreachable while locked.
  if (editing) {
    return (
      <Table.Row>
        {showKey && (
          <Table.Cell>
            <Checkbox size="sm" colorPalette="blue" defaultChecked={row.isKey} aria-label={"Key"} />
          </Table.Cell>
        )}
        <Table.Cell>
          <Input size="sm" width="full" fontWeight="600" defaultValue={row.name} aria-label={"Field"} />
        </Table.Cell>
        <Table.Cell>
          <NativeSelectRoot size="sm">
            <NativeSelectField defaultValue={row.type} items={TYPE_OPTIONS} aria-label={"Type"} />
          </NativeSelectRoot>
        </Table.Cell>
        <Table.Cell>
          <Textarea
            size="sm"
            rows={1}
            autoresize
            defaultValue={row.hint}
            placeholder={"Optional extraction hint"}
            aria-label={"AI hint"}
          />
        </Table.Cell>
        <Table.Cell textAlign="end">
          <HStack gap={1} justify="flex-end">
            <Button size="2xs" variant="ghost" colorPalette="gray" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button size="2xs" variant="solid" colorPalette="blue" onClick={() => setEditing(false)}>
              Save
            </Button>
          </HStack>
        </Table.Cell>
      </Table.Row>
    );
  }

  return (
    <Table.Row>
      {showKey && (
        <Table.Cell>
          {row.isKey && (
            <Icon asChild boxSize={4} color="blue.500">
              <CheckIcon />
            </Icon>
          )}
        </Table.Cell>
      )}
      <Table.Cell>
        <Text fontSize="sm" fontWeight="600" color="gray.fg">
          {row.name}
        </Text>
      </Table.Cell>
      <Table.Cell>
        <FieldTypeBadge type={row.type} />
      </Table.Cell>
      <Table.Cell>
        {row.hint ? (
          <Text fontSize="sm" color="fg.muted">
            {row.hint}
          </Text>
        ) : (
          <Text fontSize="sm" color="gray.400">
            —
          </Text>
        )}
      </Table.Cell>
      <Table.Cell textAlign="end">
        <LockHint locked={locked}>
          <ButtonGroup
            size="2xs"
            variant="outline"
            colorPalette="gray"
            attached
            bg="white"
            pointerEvents={locked ? "none" : undefined}
          >
            <IconButton aria-label={"Edit"} disabled={locked} onClick={() => setEditing(true)}>
              <Icon asChild>
                <PencilSquareIcon />
              </Icon>
            </IconButton>
            <IconButton aria-label={"Delete"} disabled={locked} _hover={{color: "fg.error"}}>
              <Icon asChild>
                <TrashIcon />
              </Icon>
            </IconButton>
          </ButtonGroup>
        </LockHint>
      </Table.Cell>
    </Table.Row>
  );
});

/**
 * A shared table of editable typed fields (name · type · AI hint), each row read-only by default and
 * edited inline. `showKey` adds a leading key column (checkmark / checkbox). Add lives in the footer.
 * Used by the population schema and the test extraction-fields card so they stay identical. When
 * `locked` the per-row edit/delete and the footer Add are disabled with a tooltip explaining why. No-op.
 */
export const EditableFieldsTable = memo(
  ({
    rows,
    nameHeader,
    addLabel,
    showKey = false,
    locked = false,
  }: {
    rows: EditableFieldRowData[];
    nameHeader: ReactNode;
    addLabel: ReactNode;
    showKey?: boolean;
    locked?: boolean;
  }) => (
    <>
      <Table.Root
        className="spacious"
        tableLayout="fixed"
        css={{
          "& tbody tr:last-of-type td": {borderBottom: "none"},
          "& th:first-of-type, & td:first-of-type": {paddingInlineStart: "var(--chakra-spacing-6)"},
          "& th:last-of-type, & td:last-of-type": {paddingInlineEnd: "var(--chakra-spacing-6)"},
        }}
      >
        <Table.Header>
          <Table.Row>
            {showKey && (
              <Table.ColumnHeader color="gray.500" width="72px">
                Key
              </Table.ColumnHeader>
            )}
            {/* Name flexes; the rest are fixed */}
            <Table.ColumnHeader color="gray.500">{nameHeader}</Table.ColumnHeader>
            <Table.ColumnHeader color="gray.500" width="160px">
              Type
            </Table.ColumnHeader>
            <Table.ColumnHeader color="gray.500" width="360px">
              <HStack as="span" gap={1} display="inline-flex" align="center">
                <Icon asChild boxSize={3.5} color="purple.500">
                  <SparklesIcon />
                </Icon>
                AI hint
              </HStack>
            </Table.ColumnHeader>
            <Table.ColumnHeader width="170px" />
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {rows.map(row => (
            <FieldRow key={row.id} row={row} showKey={showKey} locked={locked} />
          ))}
        </Table.Body>
      </Table.Root>

      <Box px={6} py={3} borderTop="1px solid" borderColor="gray.200">
        <LockHint locked={locked}>
          <Button
            size="xs"
            variant="solid"
            colorPalette="gray"
            disabled={locked}
            pointerEvents={locked ? "none" : undefined}
          >
            <Icon asChild>
              <PlusIcon />
            </Icon>
            {addLabel}
          </Button>
        </LockHint>
      </Box>
    </>
  ),
);
